import React, { useCallback, useEffect, useState } from 'react';
import styles from './DegreesSection.module.css';
import promotionStyles from './PromotionsSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';

const DEFAULT_OPENPAY_CONFIG = {
  name: 'Promoción ODM',
  status_after_retry: 'cancelled',
  retry_times: 2,
  repeat_unit: 'month',
  trial_days: 0,
  repeat_every: 1,
  currency: 'MXN',
};

const EMPTY_FORM = {
  textLabel: '',
  discountLabel: '',
  finishDate: '',
  rawDisscount: '',
  productIds: [],
  legacyProductNames: [],
  openpayConfig: DEFAULT_OPENPAY_CONFIG,
};

const EDUCATIONAL_CATALOGS = [
  { key: 'bachillerato', label: 'Bachillerato', path: '/bachillerato/list', productType: 'bachillerato' },
  { key: 'degrees', label: 'Licenciaturas', path: '/degrees/list', productType: 'degree' },
  { key: 'tsu', label: 'Técnico Superior Universitario', path: '/tsu/list', productType: 'tsu' },
  { key: 'master-degrees', label: 'Maestrías', path: '/master-degrees/list', productType: 'masterDegree' },
  { key: 'specialities', label: 'Especialidades', path: '/specialities/list', productType: 'speciality' },
];

function authHeaders() {
  const token = localStorage.getItem('odm_token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

const formatDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));
};

const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : '');

const parseTuitionAmount = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const cleaned = String(value || '').replace(/[^\d,.-]/g, '');
  if (!cleaned) return 0;
  const lastSeparator = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
  const decimalLength = lastSeparator >= 0 ? cleaned.length - lastSeparator - 1 : 0;
  const hasDecimals = decimalLength > 0 && decimalLength <= 2;
  const integerPart = hasDecimals ? cleaned.slice(0, lastSeparator) : cleaned;
  const decimalPart = hasDecimals ? cleaned.slice(lastSeparator + 1) : '';
  const amount = Number(`${integerPart.replace(/[.,]/g, '')}${decimalPart ? `.${decimalPart}` : ''}`);
  return Number.isFinite(amount) ? amount : 0;
};

const promotionalAmount = (tuition, discount) => Number((
  parseTuitionAmount(tuition) * 0.35 * ((100 - Number(discount || 0)) / 100)
).toFixed(2));

const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
}).format(amount);

export default function PromotionsSection() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [educationalSections, setEducationalSections] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');

  const fetchPromotions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/promotions`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudieron cargar las promociones.');
      setPromotions(data.data || []);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEducationalOffers = useCallback(async () => {
    setProductsLoading(true);
    setProductsError('');

    const results = await Promise.allSettled(
      EDUCATIONAL_CATALOGS.map(async ({ path }) => {
        const response = await fetch(`${API}${path}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || `No se pudo cargar ${path}.`);
        return Array.isArray(data.data) ? data.data : [];
      })
    );

    setEducationalSections(EDUCATIONAL_CATALOGS.map((catalog, index) => {
      const result = results[index];
      const offers = result.status === 'fulfilled'
        ? result.value
          .map((item) => ({
            id: String(item?._id || ''),
            name: typeof item?.name === 'string' ? item.name.trim() : '',
            productType: catalog.productType,
            tuition: item?.costs?.tuition || '',
          }))
          .filter((item) => item.id && item.name)
        : [];

      return {
        ...catalog,
        offers: [...new Map(offers.map((offer) => [offer.id, offer])).values()]
          .sort((a, b) => a.name.localeCompare(b.name, 'es')),
      };
    }));
    if (results.some((result) => result.status === 'rejected')) {
      setProductsError('Algunas ofertas educativas no pudieron cargarse.');
    }
    setProductsLoading(false);
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchEducationalOffers();
  }, [fetchEducationalOffers, fetchPromotions]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setView('form');
  };

  const openEdit = (promotion) => {
    const storedSubscriptions = Array.isArray(promotion.openpaySubscriptions)
      ? promotion.openpaySubscriptions
      : [];
    const productIds = storedSubscriptions.map((subscription) => String(subscription.productId));
    setEditing(promotion);
    setForm({
      textLabel: promotion.textLabel || '',
      discountLabel: promotion.discountLabel || '',
      finishDate: toDateInputValue(promotion.finishDate),
      rawDisscount: String(promotion.rawDisscount ?? ''),
      productIds,
      legacyProductNames: productIds.length === 0 && Array.isArray(promotion.products)
        ? promotion.products
        : [],
      openpayConfig: {
        ...DEFAULT_OPENPAY_CONFIG,
        ...(promotion.openpayConfig || {}),
      },
    });
    setError('');
    setView('form');
  };

  useEffect(() => {
    if (form.legacyProductNames.length === 0 || educationalSections.length === 0) return;

    const offers = educationalSections.flatMap((section) => section.offers);
    const matched = offers
      .filter((offer) => form.legacyProductNames.includes(offer.name))
      .map((offer) => offer.id);
    if (matched.length === 0) return;

    setForm((current) => ({
      ...current,
      productIds: [...new Set([...current.productIds, ...matched])],
      legacyProductNames: current.legacyProductNames
        .filter((name) => !offers.some((offer) => offer.name === name)),
    }));
  }, [educationalSections, form.legacyProductNames]);

  const toggleProduct = (productId) => {
    setForm((current) => ({
      ...current,
      productIds: current.productIds.includes(productId)
        ? current.productIds.filter((item) => item !== productId)
        : [...current.productIds, productId],
    }));
  };

  const toggleSection = (offers) => {
    const productIds = offers.map((offer) => offer.id);
    setForm((current) => {
      const allSelected = productIds.length > 0
        && productIds.every((productId) => current.productIds.includes(productId));

      return {
        ...current,
        productIds: allSelected
          ? current.productIds.filter((productId) => !productIds.includes(productId))
          : [...new Set([...current.productIds, ...productIds])],
      };
    });
  };

  const catalogProducts = educationalSections.flatMap((section) => section.offers);
  const storedProducts = (editing?.openpaySubscriptions || [])
    .filter((subscription) => !catalogProducts.some((offer) => offer.id === String(subscription.productId)))
    .map((subscription) => ({
      id: String(subscription.productId),
      name: subscription.productName,
      productType: subscription.productType,
      tuition: '',
    }));
  const productSections = storedProducts.length > 0
    ? [
      ...educationalSections,
      { key: 'saved-products', label: 'Otros productos guardados', offers: storedProducts },
    ]
    : educationalSections;
  const hasProductOptions = productSections.some((section) => section.offers.length > 0);
  const allProductOptions = productSections.flatMap((section) => section.offers);
  const selectedProducts = allProductOptions
    .filter((offer) => form.productIds.includes(offer.id));

  const updateOpenpayConfig = (field, value) => {
    setForm((current) => ({
      ...current,
      openpayConfig: { ...current.openpayConfig, [field]: value },
    }));
  };

  const handleDelete = async (promotion) => {
    if (!window.confirm(`¿Eliminar la promoción “${promotion.textLabel}”?`)) return;

    setError('');
    try {
      const response = await fetch(`${API}/promotions/${promotion._id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo eliminar la promoción.');
      await fetchPromotions();
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const productReferences = selectedProducts.map((product) => ({
        productId: product.id,
        productType: product.productType,
      }));
      const response = await fetch(
        editing ? `${API}/promotions/${editing._id}` : `${API}/promotions`,
        {
          method: editing ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            ...form,
            products: selectedProducts.map((product) => product.name),
            productReferences,
            rawDisscount: Number(form.rawDisscount),
            openpayConfig: {
              ...form.openpayConfig,
              retry_times: Number(form.openpayConfig.retry_times),
              trial_days: Number(form.openpayConfig.trial_days),
              repeat_every: Number(form.openpayConfig.repeat_every),
            },
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar la promoción.');

      setView('list');
      await fetchPromotions();
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    } finally {
      setSaving(false);
    }
  };

  if (view === 'list') {
    return (
      <div className={styles.container}>
        <div className={styles.listHeader}>
          <h1 className={styles.pageTitle}>Promociones</h1>
          <button type="button" className={styles.primaryBtn} onClick={openCreate}>
            + Nueva promoción
          </button>
        </div>

        {error && <p className={styles.errorMsg}>{error}</p>}

        {loading ? (
          <p className={styles.hint}>Cargando...</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Texto promocional</th>
                  <th>Etiqueta del descuento</th>
                  <th>Descuento</th>
                  <th>Productos aplicables</th>
                  <th>Fecha de finalización</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((promotion) => (
                  <tr key={promotion._id}>
                    <td>{promotion.textLabel}</td>
                    <td>{promotion.discountLabel}</td>
                    <td>{promotion.rawDisscount}</td>
                    <td className={promotionStyles.productsCell}>
                      {Array.isArray(promotion.products) && promotion.products.length > 0
                        ? promotion.products.join(', ')
                        : 'Sin productos asignados'}
                    </td>
                    <td>{formatDate(promotion.finishDate)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button type="button" className={styles.editBtn} onClick={() => openEdit(promotion)}>
                          Editar
                        </button>
                        <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(promotion)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {promotions.length === 0 && (
                  <tr>
                    <td colSpan={6} className={styles.hint}>Sin promociones registradas.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.listHeader}>
        <h1 className={styles.pageTitle}>
          {editing ? 'Editar promoción' : 'Nueva promoción'}
        </h1>
        <button type="button" className={styles.backBtn} onClick={() => setView('list')}>
          ← Volver
        </button>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Información de la promoción</h2>
          <div className={styles.grid2}>
            <label className={styles.label}>
              Texto promocional *
              <input
                className={styles.input}
                value={form.textLabel}
                onChange={(event) => setForm((current) => ({ ...current, textLabel: event.target.value }))}
                required
              />
            </label>
            <label className={styles.label}>
              Etiqueta del descuento *
              <input
                className={styles.input}
                value={form.discountLabel}
                onChange={(event) => setForm((current) => ({ ...current, discountLabel: event.target.value }))}
                placeholder="Ej. 65% de descuento"
                required
              />
            </label>
            <label className={styles.label}>
              Fecha de finalización *
              <input
                className={styles.input}
                type="date"
                value={form.finishDate}
                onChange={(event) => setForm((current) => ({ ...current, finishDate: event.target.value }))}
                required
              />
            </label>
            <label className={styles.label}>
              Descuento numérico *
              <input
                className={styles.input}
                type="number"
                min="0"
                max="99.99"
                step="any"
                value={form.rawDisscount}
                onChange={(event) => setForm((current) => ({ ...current, rawDisscount: event.target.value }))}
                placeholder="Ej. 65"
                required
              />
            </label>
          </div>
        </section>

        <section className={styles.formSection}>
          <h2 className={styles.sectionTitle}>Productos aplicables</h2>
          <p className={promotionStyles.productsHint}>
            Selecciona las ofertas educativas en las que puede utilizarse este descuento.
          </p>

          {productsLoading ? (
            <p className={styles.hint}>Cargando ofertas educativas...</p>
          ) : hasProductOptions ? (
            <div className={promotionStyles.sectionDropdowns}>
              {productSections.map((section) => {
                const selectedCount = section.offers
                  .filter((product) => form.productIds.includes(product.id)).length;
                const allSelected = section.offers.length > 0
                  && selectedCount === section.offers.length;
                return (
                  <details key={section.key} className={promotionStyles.sectionDropdown}>
                    <summary className={promotionStyles.sectionSummary}>
                      <span>{section.label}</span>
                      <span className={promotionStyles.sectionCount}>
                        {selectedCount} de {section.offers.length}
                      </span>
                    </summary>
                    <div className={promotionStyles.sectionOptions}>
                      {section.offers.length > 0 ? (
                        <>
                          <label className={promotionStyles.selectAllOption}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => toggleSection(section.offers)}
                            />
                            <span>
                              {allSelected ? 'Quitar toda la sección' : 'Seleccionar todas las opciones'}
                            </span>
                          </label>
                          <div className={promotionStyles.productsGrid}>
                            {section.offers.map((product) => {
                              const selected = form.productIds.includes(product.id);
                              return (
                                <label
                                  key={`${product.productType}-${product.id}`}
                                  className={`${promotionStyles.productOption} ${selected ? promotionStyles.productOptionSelected : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleProduct(product.id)}
                                  />
                                  <span>{product.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <p className={promotionStyles.emptySection}>No hay ofertas registradas en esta sección.</p>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          ) : (
            <p className={styles.hint}>No hay ofertas educativas disponibles.</p>
          )}

          {productsError && <p className={promotionStyles.productsWarning}>{productsError}</p>}
          {form.legacyProductNames.length > 0 && (
            <p className={promotionStyles.productsWarning}>
              No se encontraron estas ofertas guardadas: {form.legacyProductNames.join(', ')}.
              Vuelve a seleccionarlas antes de guardar.
            </p>
          )}
          <p className={promotionStyles.selectionCount}>
            {form.productIds.length} {form.productIds.length === 1 ? 'producto seleccionado' : 'productos seleccionados'}
          </p>
        </section>

        <section className={promotionStyles.openpaySection}>
          <div className={promotionStyles.openpayHeader}>
            <div>
              <h2 className={promotionStyles.openpayTitle}>Configuración Openpay</h2>
              <p className={promotionStyles.productsHint}>
                Al guardar una promoción vigente se crea un plan recurrente para cada producto seleccionado.
              </p>
            </div>
            <span className={promotionStyles.baseDiscountBadge}>Beca base: 65%</span>
          </div>

          <div className={styles.grid2}>
            <label className={styles.label}>
              Nombre base del plan *
              <input
                className={styles.input}
                value={form.openpayConfig.name}
                onChange={(event) => updateOpenpayConfig('name', event.target.value)}
                required
              />
            </label>
            <label className={styles.label}>
              Estado después de reintentos
              <select
                className={styles.input}
                value={form.openpayConfig.status_after_retry}
                onChange={(event) => updateOpenpayConfig('status_after_retry', event.target.value)}
              >
                <option value="cancelled">Cancelar suscripción</option>
                <option value="unpaid">Marcar como no pagada</option>
              </select>
            </label>
            <label className={styles.label}>
              Número de reintentos
              <input
                className={styles.input}
                type="number"
                min="0"
                step="1"
                value={form.openpayConfig.retry_times}
                onChange={(event) => updateOpenpayConfig('retry_times', event.target.value)}
                required
              />
            </label>
            <label className={styles.label}>
              Unidad de repetición
              <select
                className={styles.input}
                value={form.openpayConfig.repeat_unit}
                onChange={(event) => updateOpenpayConfig('repeat_unit', event.target.value)}
              >
                <option value="week">Semana</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>
            </label>
            <label className={styles.label}>
              Días de prueba
              <input
                className={styles.input}
                type="number"
                min="0"
                step="1"
                value={form.openpayConfig.trial_days}
                onChange={(event) => updateOpenpayConfig('trial_days', event.target.value)}
                required
              />
            </label>
            <label className={styles.label}>
              Frecuencia de cobro
              <input
                className={styles.input}
                type="number"
                min="1"
                step="1"
                value={form.openpayConfig.repeat_every}
                onChange={(event) => updateOpenpayConfig('repeat_every', event.target.value)}
                required
              />
            </label>
            <label className={styles.label}>
              Moneda
              <input className={promotionStyles.readonlyInput} value="MXN" readOnly />
            </label>
            <label className={styles.label}>
              Descuento adicional
              <input className={promotionStyles.readonlyInput} value={`${form.rawDisscount || 0}%`} readOnly />
            </label>
          </div>

          <div className={promotionStyles.amountsTableWrap}>
            <table className={promotionStyles.amountsTable}>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Colegiatura</th>
                  <th>Después de beca 65%</th>
                  <th>Monto promocional</th>
                  <th>ID en Openpay</th>
                </tr>
              </thead>
              <tbody>
                {selectedProducts.map((product) => {
                  const tuition = parseTuitionAmount(product.tuition);
                  const storedPlan = (editing?.openpaySubscriptions || []).find(
                    (subscription) => String(subscription.productId) === product.id
                  );
                  return (
                    <tr key={`openpay-${product.productType}-${product.id}`}>
                      <td>{product.name}</td>
                      <td>{tuition > 0 ? formatCurrency(tuition) : 'Se validará al guardar'}</td>
                      <td>{tuition > 0 ? formatCurrency(tuition * 0.35) : '—'}</td>
                      <td>{tuition > 0 ? formatCurrency(promotionalAmount(tuition, form.rawDisscount)) : '—'}</td>
                      <td className={promotionStyles.subscriptionId}>
                        {storedPlan?.subscriptionId || 'Se creará al guardar'}
                      </td>
                    </tr>
                  );
                })}
                {selectedProducts.length === 0 && (
                  <tr>
                    <td colSpan={5} className={promotionStyles.emptyAmountRow}>
                      Selecciona al menos un producto para calcular sus planes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {error && <p className={styles.errorMsg}>{error}</p>}

        <div className={styles.formActions}>
          <button type="button" className={styles.backBtn} onClick={() => setView('list')}>
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={saving || productsLoading || form.productIds.length === 0 || form.legacyProductNames.length > 0}
          >
            {saving ? 'Guardando...' : 'Guardar promoción'}
          </button>
        </div>
      </form>
    </div>
  );
}
